// Map pins (issue 1154): a marker on the board for each pin this person holds — the DM's every one, a player's only
// those the server sent (a handout of theirs, at a place they can see). A click opens the pin: its handout for anyone,
// or the DM's note. Drawn over the canvas at the board's camera, like the focus arrows; nothing here decides who sees what.
// The DM drags a pin to move it (the 2026-10-15 session pass): every pin lands at the centre of the view, so a second
// one sat on the first and a pin for the gate needed the view panned there first. A press that does not move opens it.
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { Pin, PlayerPin } from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';
import { boardBus, type BoardViewState } from './bus';
import { HandoutView } from '../shell/panels/HandoutsPanel';
import { BTN } from '../shell/ui';

const btn = BTN;

/** The board's camera, as the canvas last published it. */
function useView(): BoardViewState | null {
  const [v, setV] = useState<BoardViewState | null>(null);
  useEffect(() => boardBus.onView(setV), []);
  return v;
}

/** Past this many screen pixels a press on a pin is a drag, not a click. */
export const PIN_DRAG_PX = 4;

export function PinMarkers() {
  const { store, handouts, act, role, setNotice } = useTableState();
  const view = useView();
  const [open, setOpen] = useState<string | null>(null);
  const [dragging, setDragging] = useState<{ id: string; dx: number; dy: number } | null>(null);
  const press = useRef<{ id: string; x: number; y: number; moved: boolean } | null>(null);
  // The click the browser sends after a drag's release is not an open; a click with no drag (or the keyboard's) is.
  const dragged = useRef(false);
  const pins = Object.values((store.state.pins ?? {}) as Record<string, Pin | PlayerPin>);
  const opened = open ? pins.find((p) => p.id === open) : undefined;
  useEffect(() => { if (open && !opened) setOpen(null); }, [open, opened]);
  if (!view || !(view.view.zoom > 0)) return null;
  const handoutOf = (p: Pin | PlayerPin) => ('handout' in p ? p.handout : 'handout' in p.target ? p.target.handout : null);
  const noteOf = (p: Pin | PlayerPin) => ('target' in p && 'note' in p.target ? p.target.note : null);
  // The DM's own pins carry their target; a player's never move.
  const movable = (p: Pin | PlayerPin): p is Pin => role === 'dm' && 'target' in p;
  const down = (e: ReactPointerEvent, id: string) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    // A new press starts clean: a touch browser sends no click after a drag, so the last drag's mark would eat this tap.
    dragged.current = false;
    press.current = { id, x: e.clientX, y: e.clientY, moved: false };
  };
  const move = (e: ReactPointerEvent) => {
    const at = press.current;
    if (!at) return;
    const dx = e.clientX - at.x; const dy = e.clientY - at.y;
    if (!at.moved && Math.hypot(dx, dy) < PIN_DRAG_PX) return;
    at.moved = true;
    setDragging({ id: at.id, dx, dy });
  };
  const cancel = () => { press.current = null; setDragging(null); };
  const up = (e: ReactPointerEvent, p: Pin) => {
    const at = press.current;
    cancel();
    if (!at || at.id !== p.id || !at.moved) return;
    dragged.current = true;
    const z = view.view.zoom;
    const to = { x: p.at.x + (e.clientX - at.x) / z, y: p.at.y + (e.clientY - at.y) / z };
    act({ kind: 'pin.set', pin: { ...p, at: to } }, (r) => { if (!r.ok) setNotice(r.reason ?? 'That pin could not be moved.'); });
  };
  return (
    <>
      {pins.map((p) => {
        const moving = dragging?.id === p.id ? dragging : null;
        const sx = (p.at.x - view.view.x) * view.view.zoom + (moving?.dx ?? 0); const sy = (p.at.y - view.view.y) * view.view.zoom + (moving?.dy ?? 0);
        if (sx < -20 || sy < -20 || sx > view.viewport.width + 20 || sy > view.viewport.height + 20) return null;
        const name = p.label || (noteOf(p) !== null ? 'Note' : handouts[handoutOf(p) ?? '']?.title ?? 'Handout');
        return (
          <button key={p.id} type="button" aria-label={`Pin: ${name}`} data-testid="map-pin" title={name}
            className={`pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-white shadow ${noteOf(p) !== null ? 'bg-stone-600' : 'bg-amber-600'}`}
            style={{ left: sx, top: sy, touchAction: movable(p) ? 'none' : undefined }}
            onPointerDown={movable(p) ? (e) => down(e, p.id) : undefined} onPointerMove={movable(p) ? move : undefined}
            onPointerUp={movable(p) ? (e) => up(e, p as Pin) : undefined} onPointerCancel={movable(p) ? cancel : undefined}
            onClick={() => { if (dragged.current) { dragged.current = false; return; } setOpen(p.id); }} />
        );
      })}
      {opened && (
        <div role="dialog" aria-label="Pin" className="pointer-events-auto absolute right-2 top-12 max-h-[70%] w-80 overflow-y-auto vtt-popover p-3 text-sm shadow-lg" data-testid="pin-dialog">
          {noteOf(opened) !== null
            ? <><h3 className="vtt-title">{opened.label || 'Note'}</h3><p className="whitespace-pre-wrap break-words">{noteOf(opened)}</p><p className="text-xs text-stone-400">Yours alone: players never see a note pin.</p></>
            : handouts[handoutOf(opened) ?? ''] ? <HandoutView handout={handouts[handoutOf(opened)!]!} /> : <p>That handout is no longer here.</p>}
          <button type="button" className={`${btn} mt-2`} onClick={() => setOpen(null)}>Close</button>
        </div>
      )}
    </>
  );
}
